'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { SectionContainer } from '@/components/ui/SectionContainer';
import { components } from '@/types/strapi';

export default function PartnershipTypes({
	heading,
	subheading,
	partnerTypes,
	isFirst = false,
}: components['schemas']['DynamicZonePartnershipTypesComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	return (
		<SectionContainer className="partnership-types-section py-8">
			<noscript>
				<style>{`
					.partnership-types-section h2[style],
					.partnership-types-section h3[style],
					.partnership-types-section p[style],
					.partnership-types-section div[style*='opacity'],
					.partnership-types-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mb-8 text-center">
				<SectionHeading
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					viewport={{ once: true }}
					className="mb-6 bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
					{heading}
				</SectionHeading>
				<motion.p
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.2 }}
					viewport={{ once: true }}
					className="mx-auto max-w-3xl text-lg text-gray-600">
					{subheading}
				</motion.p>
			</div>
			<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
				{partnerTypes?.map((type, index) => (
					<motion.div
						key={index}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.2 }}
						viewport={{ once: true }}
						className="group relative rounded-2xl border border-gray-100 bg-white p-4 shadow-lg transition-all duration-300 hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5">
						<div className="mb-6 flex items-center gap-4">
							<div className="h-16 w-16 rounded-xl bg-primary/5 p-3 transition-colors duration-300 group-hover:bg-primary/10">
								<Image
									src={type?.icon?.url || ''}
									alt={type?.icon?.alternativeText || ''}
									width={28}
									height={28}
									className="h-full w-full object-contain"
								/>
							</div>
							<h3 className="text-2xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-primary">
								{type.title}
							</h3>
						</div>

						<p className="mb-12 min-h-[80px] text-gray-600">
							{type.description}
						</p>

						<a
							href={type.ctaLink}
							className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-lg bg-primary/5 px-4 py-2 font-semibold text-primary transition-all duration-300 hover:gap-3 hover:bg-primary/10">
							{type.ctaText}
							<ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
						</a>

						<div className="absolute inset-x-0 -bottom-px mx-auto h-1 w-[80%] rounded-full bg-primary/0 transition-colors duration-300 group-hover:bg-primary/20" />
					</motion.div>
				))}
			</div>
		</SectionContainer>
	);
}
